const USD_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const USD_FINE_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 6,
});

/**
 * Format authoritative money fields carried as integer USD cents.
 */
export function formatUsdCents(cents: number, signed = false): string {
  if (!Number.isFinite(cents)) return '—';
  const base = USD_FORMATTER.format(Math.abs(cents) / 100);
  if (!signed || cents === 0) return base;
  return cents > 0 ? `+${base}` : `-${base}`;
}

/**
 * Format very small costs that are still transported as USD cents.
 */
export function formatFineUsdCents(cents: number): string {
  if (!Number.isFinite(cents)) return '—';
  return USD_FINE_FORMATTER.format(Math.abs(cents) / 100);
}

export type ParseUsdToCentsResult =
  | { ok: true; cents: number; normalized: string }
  | { ok: false };

/**
 * Parse an operator-entered USD decimal into signed integer cents.
 */
export function parseUsdInputToCents(raw: string): ParseUsdToCentsResult {
  const trimmed = raw.trim();
  if (!/^-?(?:\d+|\d+\.\d{1,2}|\.\d{1,2})$/.test(trimmed)) {
    return { ok: false };
  }
  const sign = trimmed.startsWith('-') ? -1 : 1;
  const unsigned = sign === -1 ? trimmed.slice(1) : trimmed;
  const [wholeRaw = '', fracRaw = ''] = unsigned.split('.');
  const whole = wholeRaw.length === 0 ? 0 : Number(wholeRaw);
  const frac = Number(fracRaw.padEnd(2, '0'));
  if (!Number.isSafeInteger(whole) || !Number.isSafeInteger(frac)) {
    return { ok: false };
  }
  const cents = sign * (whole * 100 + frac);
  if (!Number.isSafeInteger(cents)) {
    return { ok: false };
  }
  const normalizedMagnitude = `${whole}.${fracRaw.padEnd(2, '0')}`;
  return {
    ok: true,
    cents,
    normalized: `${sign === -1 ? '-' : ''}${normalizedMagnitude}`,
  };
}
